import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Lock, CheckCircle2, AlertTriangle, XCircle, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useSeo } from "../lib/seo";
import { LogoHorizontal } from "../components/Logo";
import { SignaturePad } from "../components/SignaturePad";
import { Card, Field, inputCls, btnPrimary, errMsg } from "../components/institution/ui";

const OUTCOMES = [
  { id: "approved", label: "Approve — proceed as planned", Icon: CheckCircle2, cls: "border-emerald-500 bg-emerald-50 text-emerald-800" },
  { id: "conditions", label: "Approve with conditions", Icon: AlertTriangle, cls: "border-amber-500 bg-amber-50 text-amber-800" },
  { id: "declined", label: "Do not approve / postpone", Icon: XCircle, cls: "border-red-500 bg-red-50 text-red-800" },
];

function CodeStep({ info, onOpen }) {
  const [code, setCode] = useState("");
  return (
    <Card className="max-w-md mx-auto" data-testid="reply-code-step">
      <form onSubmit={(e) => { e.preventDefault(); if (code.length === 4) onOpen(code); }}>
      <p className="font-heading text-xl font-bold text-[#0B2545] flex items-center gap-2"><Lock className="w-5 h-5 text-[#1C6FE3]" /> Medical consultation request</p>
      <p className="text-sm text-slate-600 mt-2">From Dr. {info.dentist}{info.clinic ? ` · ${info.clinic}` : ""}. Enter the 4-digit verification code printed on the report.</p>
      <input inputMode="numeric" maxLength={4} className={`${inputCls} mt-4 text-center text-2xl tracking-[0.5em] h-14`} value={code}
        onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} data-testid="reply-code-input" />
      <button type="submit" className={`${btnPrimary} w-full mt-4 h-11`} disabled={code.length !== 4} data-testid="reply-code-submit">Open request</button>
      </form>
    </Card>
  );
}

function Summary({ c }) {
  const p = c.patient;
  return (
    <Card data-testid="reply-summary">
      <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Patient</p>
      <p className="font-heading text-xl font-bold text-[#0B2545]" data-testid="reply-patient-name">{p.name || "—"}</p>
      <p className="text-sm text-slate-600">{[p.age && `${p.age} y`, p.sex, p.weight_kg && `${p.weight_kg} kg`].filter(Boolean).join(" · ")} — referred by Dr. {c.dentist}{c.clinic ? `, ${c.clinic}` : ""}</p>
      <div className="mt-4 space-y-1.5 text-sm text-slate-700">
        <p><b>Planned dental procedure:</b> {c.procedure}</p>
        <p><b>Conditions:</b> {c.conditions.join(", ") || "None recorded"}</p>
        <p><b>Medications:</b> {c.medications.join(", ") || "None recorded"}</p>
        {c.labs.length > 0 && <p><b>Results:</b> {c.labs.map((l) => `${l.name.split(" — ").pop()} ${l.value} ${l.unit}`).join(" · ")}</p>}
      </div>
      <div className="mt-4 rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm">
        <p className="font-semibold text-[#0B2545]">{c.verdict}</p>
        {c.basis.map((b) => <p key={b} className="text-xs text-slate-600 mt-1">• {b}</p>)}
      </div>
      {c.question && <p className="mt-2 text-sm"><b>Dentist's question:</b> {c.question}</p>}
      {c.fingerprint && <p className="mt-3 text-xs text-slate-500" data-testid="reply-fingerprint">Report fingerprint: <b className="font-mono text-slate-700">{c.fingerprint}</b> — should match the code printed on the referral report.</p>}
    </Card>
  );
}

const tid = (role) => role.toLowerCase().replace(/[^a-z0-9]+/g, "-");

function Answered({ slot }) {
  const r = slot.response;
  const o = OUTCOMES.find((x) => x.id === r.outcome);
  return (
    <Card data-testid={`reply-slot-done-${tid(slot.role)}`} id={`slot-${tid(slot.role)}`}>
      <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{slot.role}</p>
      <p className="text-sm text-slate-600 mt-1">{slot.question}</p>
      <p className={`mt-3 inline-flex items-center gap-2 rounded-lg border-2 px-3 py-2 text-sm font-semibold ${o.cls}`}><o.Icon className="w-4 h-4" /> Replied — {o.label}</p>
      <p className="text-sm text-slate-700 mt-2">Dr. {r.name} ({r.specialty}){r.license ? ` · Lic. ${r.license}` : ""} · {new Date(r.date).toLocaleString("en-GB")}</p>
      {r.conditions && <p className="text-sm text-slate-700 mt-1"><b>Conditions:</b> {r.conditions}</p>}
      {r.note && <p className="text-sm text-slate-700 mt-1"><b>Note:</b> {r.note}</p>}
    </Card>
  );
}

function ReplyForm({ slot, code, token, onDone }) {
  const [f, setF] = useState({ role: slot.role, outcome: "", conditions: "", note: "", name: "", specialty: "", license: "", signature: null });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => { const v = e.target.value; setF((x) => ({ ...x, [k]: v })); };
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/public/referral/${token}/respond`, { ...f, code });
      toast.success("Reply sent to the dentist");
      onDone({ ...f, date: new Date().toISOString() }, data.closed);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const ready = f.role && f.outcome && f.name.length > 1 && f.specialty.length > 1 && f.signature && (f.outcome !== "conditions" || f.conditions.length > 2);
  return (
    <Card data-testid={`reply-slot-${tid(slot.role)}`} id={`slot-${tid(slot.role)}`}>
      <p className="text-xs uppercase tracking-wider text-amber-600 font-semibold">Awaiting reply · {slot.role}</p>
      <p className="font-heading font-semibold text-[#0B2545] mt-1">{slot.question}</p>
      <div className="grid gap-2 mt-3">
        {OUTCOMES.map(({ id, label, Icon, cls }) => (
          <button key={id} type="button" onClick={() => setF((x) => ({ ...x, outcome: id }))} data-testid={`reply-outcome-${id}`}
            className={`flex items-center gap-2 rounded-xl border-2 px-4 py-3 text-sm font-semibold text-left transition-colors ${f.outcome === id ? cls : "border-slate-200 text-slate-700 hover:bg-slate-50"}`}>
            <Icon className="w-5 h-5" /> {label}
          </button>
        ))}
      </div>
      {f.outcome === "conditions" && <textarea rows={3} className={`${inputCls} h-auto py-2 mt-3`} placeholder="Conditions (e.g. omit dabigatran morning dose; amoxicillin 2 g 1 h before)" value={f.conditions} onChange={set("conditions")} data-testid="reply-conditions" />}
      <textarea rows={2} className={`${inputCls} h-auto py-2 mt-3`} placeholder="Notes for the dentist (optional)" value={f.note} onChange={set("note")} data-testid="reply-note" />
      <div className="grid sm:grid-cols-3 gap-3 mt-3">
        <Field label="Your name"><input className={inputCls} value={f.name} onChange={set("name")} data-testid="reply-name" /></Field>
        <Field label="Specialty"><input className={inputCls} value={f.specialty} onChange={set("specialty")} data-testid="reply-specialty" /></Field>
        <Field label="License no. (optional)"><input className={inputCls} value={f.license} onChange={set("license")} data-testid="reply-license" /></Field>
      </div>
      <div className="mt-3"><Field label="Signature"><SignaturePad onChange={(sig) => setF((x) => ({ ...x, signature: sig }))} testid="reply-signature" /></Field></div>
      <button type="button" className={`${btnPrimary} w-full mt-5 h-11`} disabled={!ready || busy} onClick={submit} data-testid="reply-submit">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4" /> Send reply to the dentist</>}
      </button>
    </Card>
  );
}

function Slots({ c, code, token, dentist, onDone }) {
  const done = c.slots.filter((s) => s.response).length;
  const all = c.allDone || done === c.slots.length;
  return (
    <div className="grid lg:grid-cols-[1fr_1.2fr] gap-5 items-start">
      <Summary c={c} />
      <div className="space-y-4">
        {!all && c.slots.length > 1 && (
          <div className="flex flex-wrap gap-2" data-testid="reply-jump">
            {c.slots.map((sl) => <a key={sl.role} href={`#slot-${tid(sl.role)}`} className={`text-xs px-2.5 py-1 rounded-full border ${sl.response ? "border-emerald-300 bg-emerald-50 text-emerald-800" : "border-amber-300 bg-amber-50 text-amber-800"}`}>{sl.response ? "✓" : "⏳"} {sl.role}</a>)}
          </div>
        )}
        <p className="text-sm font-semibold text-slate-700" data-testid="reply-progress">{all ? c.slots.length : done} of {c.slots.length} specialist {c.slots.length === 1 ? "reply" : "replies"} received</p>
        {all && (
          <Card className="text-center" data-testid="reply-done">
            <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto" />
            <p className="font-heading text-lg font-bold text-[#0B2545] mt-2">All replies received — thank you</p>
            <p className="text-sm text-slate-600 mt-1">Dr. {dentist} has been notified and the treatment decision was updated.</p>
            <p className="text-xs text-slate-400 mt-4">Treat dental patients yourself? <a href="/" className="text-[#1C6FE3] underline">Discover DentiCision</a></p>
          </Card>
        )}
        {c.slots.map((sl) => sl.response ? <Answered key={sl.role} slot={sl} /> : !all && <ReplyForm key={sl.role} slot={sl} code={code} token={token} onDone={onDone} />)}
      </div>
    </div>
  );
}

export default function SpecialistReply() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [c, setC] = useState(null);
  const [code, setCode] = useState("");

  useSeo({ title: "Specialist reply — DentiCision", path: "/r", noindex: true });
  useEffect(() => { api.get(`/public/referral/${token}`).then(({ data }) => setInfo(data)).catch((e) => setError(errMsg(e))); }, [token]);
  const open = async (cd) => {
    try {
      const { data } = await api.post(`/public/referral/${token}/open`, { code: cd });
      setC(data);
      setCode(cd);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  let body;
  if (error) body = <Card className="max-w-md mx-auto text-center" data-testid="reply-error">{error}</Card>;
  else if (!info) body = <Loader2 className="w-6 h-6 animate-spin text-slate-400 mx-auto" />;
  else if (info.superseded) body = <Card className="max-w-md mx-auto text-center" data-testid="reply-superseded">The patient's case changed after this report was printed, so this link no longer accepts replies. Please ask the dentist for the updated referral report.</Card>;
  else if (!info.active) body = <Card className="max-w-md mx-auto text-center" data-testid="reply-expired">This link has expired or the request has already been answered. Please contact the dentist.</Card>;
  else if (!c) body = <CodeStep info={info} onOpen={open} />;
  else body = <Slots c={c} code={code} token={token} dentist={info.dentist} onDone={(resp, closed) => {
    const next = { ...c, slots: c.slots.map((sl) => (sl.role === resp.role ? { ...sl, response: resp } : sl)) };
    if (closed) setC({ ...next, allDone: true }); else { setC(next); open(code); }
  }} />;
  return (
    <div className="min-h-screen bg-slate-50" data-testid="specialist-reply-page">
      <header className="bg-white border-b border-slate-200"><div className="max-w-5xl mx-auto px-4 h-20 flex items-center"><LogoHorizontal size={56} /></div></header>
      <main className="max-w-5xl mx-auto px-4 py-10">{body}</main>
    </div>
  );
}
